import { OverlayRef } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import { Component, ChangeDetectionStrategy, AfterContentInit, Inject, ChangeDetectorRef, Input, ElementRef, ViewChild } from '@angular/core';
import { VALUE_DOWN, VALUE_ENTER, VALUE_ESCAPE, VALUE_LEFT, VALUE_RIGHT, VALUE_TAB, VALUE_UP } from '@wizeoo/akm-core-lib';

import { WizDateUtils, WizNumberUtils } from '@wizeoo/akm-core-lib';

/** Objet pour la sélection des heures minutes */
export class TimeStepVO {

  constructor(pValue: string) {
    this.value = pValue;
  }
  /** Valeur */
  public value: string;
  
  /** Valeur non sélectionnable */
  public disabled: boolean;
}

@Component({
  selector:'wiz-timepicker',
  templateUrl: './wiz-timepicker.html',
  styleUrls: ['./wiz-timepicker.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WizTimepicker implements AfterContentInit {
  
  // la colonne des heures
  @ViewChild('hourDiv', {static: true, read: ElementRef}) protected _hourDiv: ElementRef;
  // la colonne des heures
  @ViewChild('minuteDiv', {static: true, read: ElementRef}) protected _minuteDiv: ElementRef;
  // la colonne des meridiem
  @ViewChild('meridiemDiv', {static: false, read: ElementRef}) protected _meridiemDiv: ElementRef;

  /* ##### MERIDIEM #####  */

  /** Valeur pour AM */
  private readonly _AM: string = 'AM';
  /** Valeur pour PM */
  private readonly  _PM: string = 'PM';
  /** Valeur sélectionnée pour le meridiem */
  public meridiemValue: string = this._AM;
  /** Mode heure avec meridiem (ou pas) */
  private _meridiemMode: boolean = true;
  /** Tableau des meridiems */
  public meridiems:Array<TimeStepVO>;

  /* ####### SELECTIONS ######## */

  /** Tableau des heures */
  public hours: Array<TimeStepVO>;
  /** Heure sélectionnée affichée */
  private _displayedHour: string;
  /** Tableau des minutes */
  public minutes: Array<TimeStepVO>;
  /** Ecart entre chaque minute sélectionnable */
  private _minuteStep: number = 1;
  /** Heure sélectionnée: string au format HHmm */
  private _wizHourValue: string;
  /** Heure sélectionnée (mode 24h) */
  private _selectedHour: string;
  /** Minute sélectionnée */
  private _selectedMinute: string;

  /** Heure minimale */
  private _minHour: string;
  /** Heure maximale */
  private _maxHour: string;

  /* ######### SPINNER ########## */
  /** Mode heures du spinner en cours de mouvement (1 : heures, 2: minutes, 3: meridiem) */
  private _spinnerMode: number;
  public readonly SPINNER_HOUR_MODE: number = 1;
  public readonly SPINNER_MINUTE_MODE: number = 2;
  public readonly SPINNER_MERIDIEM_MODE: number = 3;
  /** Offset à atteindre pour passage à l'élément précédent ou suivant dans les heures ou les minutes*/
  private readonly _SPINNER_OFFSET: number = 10;
  /** Pointeur de fonction de fin de drag pour faciliter le removeEventListener */
  private _dragEndFunc: any;
  /** Pointeur de fonction de début de drag pour faciliter le removeEventListener */
  private _draggingFunc: any;
  /** Position de départ de drag */
  private _start: number;

  /**
   * Callback de sélection d'heure: callback(pHour:string)
   */
  public wizValidHourCallBack: Function;

  /** Affichage du bouton de validation */
  private _wizShowValidButton: boolean = true;

  /** Constructeur */
  constructor(protected cdref: ChangeDetectorRef, public elementRef: ElementRef,
              @Inject(DOCUMENT) protected document: Document,
              protected overlayRef?: OverlayRef) {
                this.elementRef.nativeElement.addEventListener('keydown', (e: KeyboardEvent) => this.keyDownHandler(e));
              }


  /** Getter/setter du pas de minutes */
  public get wizMinuteStep(): number {
    return this.getMinuteStep();
  }
  @Input()
  public set wizMinuteStep(value: number) {
    this.setMinuteStep(value);
  }
  protected getMinuteStep(): number {
    return this._minuteStep;
  }
  protected setMinuteStep(value: number): void {
    if (value && value > 0 && value <= 60) {
      this._minuteStep = value;
    }
  }

  /** Getter/setter du mode meridiem */
  public get wizMeridiemMode(): boolean {
    return this.getMeridiemMode();
  }
  @Input()
  public set wizMeridiemMode(value: boolean) {
    this.setMeridiemMode(value);
  }
  protected getMeridiemMode(): boolean {
    return this._meridiemMode;
  }
  protected setMeridiemMode(value: boolean): void {
    this._meridiemMode = value;
  }

  /** Retourne vrai si matin sélectionné en mode meridiem */
  private isAM():boolean {
    return this.meridiemValue === this._AM;
  }

  /** Getter/setter Affichage du bouton de validation */
  public get wizShowValidButton(): boolean {
    return this.getWizShowValidButton();
  }
  @Input()
  public set wizShowValidButton(value: boolean) {
    this.setWizShowValidButton(value);
  }
  protected getWizShowValidButton(): boolean {
    return this._wizShowValidButton;
  }
  protected setWizShowValidButton(value: boolean): void {
    this._wizShowValidButton = value;
  }

  /** Getter/setter de la valeur sélectionnée */
  public get wizHourValue(): string {
    return this.getWizHourValue();
  }
  @Input()
  public set wizHourValue(pValue: string) {
    this.setWizHourValue(pValue);
  }
  protected getWizHourValue(): string {
    this._wizHourValue = this._selectedHour + this._selectedMinute;
    return this._wizHourValue;
  }
  protected setWizHourValue(pValue: string): void {
    this._wizHourValue = undefined;
    this._selectedHour = undefined;
    this._selectedMinute = undefined;
    if (pValue && pValue.length == 4) {        
      const hourString = pValue.substr(0, 2);
      const minuteString = pValue.substr(2, 2);
      if (WizDateUtils.isValidHour(hourString) && WizDateUtils.isValidMinute(minuteString)) {
        this._wizHourValue = pValue;
        this.setSelectedHour(hourString);
        this._selectedMinute = minuteString;
      }
    }
  }

  /** Heure maximale */
  public get wizMaxHour(): string {
      return this.getWizMaxHour();
  }
  @Input()
  public set wizMaxHour(val: string) {
      this.setWizMaxHour(val);
  }
  protected getWizMaxHour(): string {
    return this._maxHour;
  }
  protected setWizMaxHour(val: string): void {
    this._maxHour = val;
  }

  /** Heure minimale */
  public get wizMinHour(): string {
      return this.getWizMinHour();
  }
  @Input()
  public set wizMinHour(val: string) {
      this.setWizMinHour(val);
  }
  protected getWizMinHour(): string {
    return this._minHour;
  }
  protected setWizMinHour(val: string): void {
    this._minHour = val;
  }


  /**
   * Sélection d'heure
   * @param pValue heure à sélectionner
   */
  private setSelectedHour(pValue: string): void {
    this._selectedHour = pValue;
    this._displayedHour = pValue;
    if (this.wizMeridiemMode) {
      const hourNumber: number = WizNumberUtils.parseNumber(this._selectedHour);
      if (hourNumber > 11) {
        this.meridiemValue = this._PM;
        // On est probablement passé à un nombre sur 1 digit, donc padleft pour passer sur 2 digits
        this._displayedHour =  hourNumber === 12 ? '12' : WizDateUtils.numberOnTwoDigits(hourNumber - 12);
      } else {
        this.meridiemValue = this._AM;
        this._displayedHour =  hourNumber === 0 ? '12' : this._selectedHour;
      }
    }
  }

  /**
   * Initialisation des listes d'heures et minutes et des valeurs sélectionnées
   */
  ngAfterContentInit(): void {
    this.initCollections();
    this.selectWizHour();
  }

  // public ngAfterViewInit() {
  //   setTimeout(() => {
  //     this.wizFocus();
  //   }, 0);
  // }

  /**
   * Initialisation des collections d'heures et de minutes
   */
  private initCollections():void {
    this.hours = [];
    this.minutes = [];
    this.meridiems = [new TimeStepVO(""), new TimeStepVO(this._AM), new TimeStepVO(this._PM)];
    const minHour:number = this._meridiemMode ? 1 : 0;
    const maxHour:number = this._meridiemMode ? 12 : 23;
    for (let i: number = minHour; i <= maxHour; i++) {
      this.hours.push(new TimeStepVO(WizDateUtils.numberOnTwoDigits(i)));
    }
    for (let i: number = 0; i < 60; i = i + this._minuteStep) {
      this.minutes.push(new TimeStepVO(WizDateUtils.numberOnTwoDigits(i)));
    }
    if (this.minutes.length < 3) {
      if (this.minutes.length === 1) {
        this.minutes.push(new TimeStepVO(""));
      }
      this.minutes.unshift(new TimeStepVO(""));
    }
  }

  /**
   * Sélection visuelle de la valeur horaire
   */
  private selectWizHour(): void {
    // Valeur par défaut: maintenant ou une des 2 bornes si on est en dehors
    let defaultHour: string = this._wizHourValue;
    if (!defaultHour) {
      defaultHour = WizDateUtils.dateToString(WizDateUtils.TODAY, WizDateUtils.TIME_MASK);
    }
    // Check si on est hors bornes
    if (this.wizMinHour && WizDateUtils.isHourBefore(defaultHour, this.wizMinHour)) {
      defaultHour = this.wizMinHour;
    } else if (this.wizMaxHour && WizDateUtils.isHourAfter(defaultHour, this.wizMaxHour)) {
      defaultHour = this.wizMaxHour;
    }
    // Remplacement de l'heure s'il y a changement
    if (defaultHour !== this.wizHourValue) {
      this.setWizHourValue(defaultHour);
    }
    // Sélection de l'heure
    let selected: string = this.hours[1].value;
    while (selected !== this._displayedHour) {
      this.hours.push(this.hours.shift());
      selected = this.hours[1].value;
    }
    //Sélection des minutes
    selected = this.minutes[1].value;
    let minuteFound: boolean = false;
    // minute à sélectionner au format number, nécessaire à la méthode checkSelectedMinute
    const selectedMinuteNumber = WizNumberUtils.parseNumber(this._selectedMinute);
    minuteFound = this.checkSelectedMinute(selected, selectedMinuteNumber);
    while (!minuteFound) {
      this.minutes.push(this.minutes.shift());
      selected = this.minutes[1].value;
      minuteFound = this.checkSelectedMinute(selected, selectedMinuteNumber);
    }
    this.checkNextPreviousDisabled();
    // Meridiem le cas échéant
    if (this._meridiemMode && this.meridiemValue !== this.meridiems[1].value) {
      if (this.meridiemValue == this._AM) {
        this.meridiems.unshift(this.meridiems.pop());
      } else {
        this.meridiems.push(this.meridiems.shift());
      }
    }
  }

  /**
   * Checke si un pas de minute est bien visuellement sélectionné
   * @param pCheckedMinute Pas de minute testé
   * @param pMinuteSelectionNumber Minute à sélectionner au format numérique
   * @return boolean
   */
  private checkSelectedMinute(pCheckedMinute: string, pMinuteSelectionNumber: number): boolean {
    let result: boolean = false;
    if (pCheckedMinute === this._selectedMinute) {
      result = true;
    } else if (WizNumberUtils.parseNumber(pCheckedMinute) < pMinuteSelectionNumber) {
      // Le prochain pas peut dépasser (si wizMinuteStep > 1), si c'est le cas on s'arrête
      let nextMinuteStep: number = WizNumberUtils.parseNumber(this.minutes[2].value);
      if (nextMinuteStep === 0 ||
          (nextMinuteStep > pMinuteSelectionNumber)) {
        result = true; 
      }
    }
    return result;
  }  

  /**
   * Handler de wheel pour le défilement des heures ou des minutes
   * @param event Evenement wheel
   * @param pHourMode vrai si concerne les heures
   */
  public onWheel(event: any, pHourMode: boolean): void {
    event.preventDefault();
    if (event.deltaY > 0 ) {
      this.selectNext(pHourMode);
    } else if (event.deltaY < 0) {
      this.selectPrevious(pHourMode);
    }
  }

  /**
   * Handler de wheel pour le défilement des heures ou des minutes
   * @param event Evenement wheel
   */
  public onMeridiemWheel(event: any): void {
    event.preventDefault();
    if (this.meridiemValue === this._PM) {
      this.selectPreviousMeridiem();
    } else if (event.deltaY < 0) {
      this.selectNextMeridiem();
    }
  }

  /**
   * Sélection du meridiem précédent (AM)
   */
  public selectPreviousMeridiem(): void {
    if (this.meridiemValue === this._PM) {
      if (this.switchMeridiem(this._AM)) {
        this.meridiems.unshift(this.meridiems.pop());
      }
    }
  }

  /**
   * Sélection du meridiem suivant (PM)
   */
  public selectNextMeridiem(): void {
    if (this.meridiemValue === this._AM) {
      if (this.switchMeridiem(this._PM)) {
        this.meridiems.push(this.meridiems.shift());
      }
    }
  }

  /**
   * Changement de meridiem
   * @param newMeridiem nouvelle valeur
   * @return true si l'heure obtenue est autorisée
   */
  private switchMeridiem(newMeridiem: string): boolean {
    let newHourOk: boolean = true;
    if (newMeridiem !== this.meridiemValue) {
      let previsionalHour: string = this._displayedHour;
      if (this.wizMeridiemMode) {
        if (!this.isAM()) {
          if (this._displayedHour === '12') {
            previsionalHour = '00';
          }
        } else if (this._displayedHour !== '12') {
          previsionalHour = (WizNumberUtils.parseNumber(this._displayedHour) + 12).toString();
        }
      }
      if (this.wizMinHour || this.wizMaxHour) {
        let previsionalValue: string = previsionalHour + this._selectedMinute;
        if (this.wizMinHour && WizDateUtils.isHourBefore(previsionalValue, this.wizMinHour)) {
          this.wizHourValue = this.wizMinHour;
          this.selectWizHour();
          newHourOk = false;
        } else if (this.wizMaxHour && WizDateUtils.isHourAfter(previsionalValue, this.wizMaxHour)) {
          this.wizHourValue = this.wizMaxHour;
          this.selectWizHour();
          newHourOk = false;
        }
      }
      if (newHourOk) {
        this.meridiemValue = newMeridiem;
        this._selectedHour = previsionalHour;
        this.checkNextPreviousDisabled();
      }
    }
    return newHourOk;
  }

  /**
   * Renseigne la validité des éléments précédents et suivants selon les bornes
   */
  private checkNextPreviousDisabled(): void {
    this.hours[0].disabled = !this.isSideElementValid(true, false);
    this.hours[2].disabled = !this.isSideElementValid(true, true);
    this.minutes[0].disabled = !this.isSideElementValid(false, false);
    this.minutes[2].disabled = !this.isSideElementValid(false, true);
  }

  /**
   * Sélection de la valeur précédente
   * @param pHourMode vrai si concerne les heures
   */
  public selectPrevious(pHourMode: boolean): void {
    const valueList: Array<TimeStepVO> = pHourMode ? this.hours : this.minutes;
    if (!valueList[0].disabled) {
      valueList.unshift(valueList.pop());
      this.selectElement(pHourMode);
    }
  }

  /**
   * L'élément précédent ou suivant peut-il être sélectionné?
   * @param pHourMode heure si vrai, sinon minute
   * @param pNextMode élément suivant si vrai, sinon le précédent
   * @return boolean
   */
  private isSideElementValid(pHourMode: boolean, pNextMode: boolean): boolean {
    const sideElementIndex: number = pNextMode ? 2 : 0;
    const sideElement: string = pHourMode ? this.hours[sideElementIndex].value : this.minutes[sideElementIndex].value;
    if (!sideElement) {
      return false;
    }
    if (this.wizMinHour || this.wizHourValue) {
      let previsionalHour: string = null;
      if (pHourMode) {
        previsionalHour = this.getNoMeridiemHour(sideElement) + this._selectedMinute;
      } else {
        previsionalHour = this._selectedHour + sideElement;
      }
      if (this.wizMinHour && WizDateUtils.isHourBefore(previsionalHour, this.wizMinHour)) {
        return false;
      }
      if (this.wizMaxHour && WizDateUtils.isHourAfter(previsionalHour, this.wizMaxHour)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Retourne la valeur formatée HH 24h d'une heure au format selon le mode 12h/24h actuel
   * @param pHour Heure au format actuel
   * @return string l'heure au format HH
   */
  private getNoMeridiemHour(pHour: string): string {
    let result = pHour;
    // Format 12h à retourner en 24h?
    if (this._meridiemMode) {
      // Le matin, 12 AM = 00h
      if (this.isAM()) {
        if (result === '12') {
          result = '00';
        }
      } else if (result !== '12') { // L'après midi, 12 PM = 12h, donc pas de changement, sinon on ajoute 12
        result = (WizNumberUtils.parseNumber(result) + 12).toString();
      }
    }
    return result;
  }

   /**
   * Sélection de la valeur suivante
   * @param pHourMode vrai si concerne les heures
   */
  public selectNext(pHourMode: boolean): void {
    const valueList: Array<TimeStepVO> = pHourMode ? this.hours : this.minutes;
    if (!valueList[2].disabled) {
      valueList.push(valueList.shift());
      this.selectElement(pHourMode);
    }
  }

  /**
   * Sélection de l'élément central
   * @param pHourMode vrai si concerne les heures
   */
  private selectElement(pHourMode: boolean): void {
    if (pHourMode) {
      this._displayedHour = this.hours[1].value;
      this._selectedHour = this.getNoMeridiemHour(this._displayedHour);
    } else {
     this._selectedMinute = this.minutes[1].value;
    }
    this.checkNextPreviousDisabled();
  }

  /**
   * Handler de début de drag
   * @param e Evenement écouté (mouse down ou touch move d'où le non typage)
   * @param pMode Mode heure, minute ou meridiem
   */
  public onDragStart(e: any, pMode: number): void {
    this._spinnerMode = pMode;
    // J'alimente la position de départ
    this._start = e.touches ? e.touches[0].clientY : e.clientY;
    // init des pointeurs de fonction début/fin de drag
    this._dragEndFunc = this.onDragEnd.bind(this);
    this._draggingFunc = this.onDragging.bind(this);

     // Ajout des listeners souris/touch
    this.document.addEventListener('mouseup', this._dragEndFunc, false);
    this.document.addEventListener('mousemove', this._draggingFunc, false);
    this.document.addEventListener('touchend', this._dragEndFunc, false);
    this.document.addEventListener('touchmove', this._draggingFunc, false);

    // Disable highlighting while dragging
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    if (e.preventDefault) {
      e.preventDefault();
    }

    e.cancelBubble = true;
    e.returnValue = false;

  }

  /**
   * Handler de mouse move ou touch move pour la sélection d'heures
   *
   * Détermination de la direction du drag et emission du mouvement via l'observable dédié
   * @param e Evenement écouté (mouse move ou touch move d'où le non typage)
   */
  private onDragging(e: any) {
    const offset: number = this._start - (e.touches ? e.touches[0].clientY : e.clientY);
    if (offset >= this._SPINNER_OFFSET || offset <= 0-this._SPINNER_OFFSET) {
      this._start -= offset;
      if (offset > 0) {
        if (this._spinnerMode === this.SPINNER_MERIDIEM_MODE) {
          if (this.meridiemValue === this._AM) {
            if (this.switchMeridiem(this._PM)) {
              this.meridiems.push(this.meridiems.shift());
            }
          }
        } else {
          this.selectNext(this._spinnerMode === this.SPINNER_HOUR_MODE);
        }
      } else {
        if (this._spinnerMode === this.SPINNER_MERIDIEM_MODE) {
          if (this.meridiemValue === this._PM) {
            if (this.switchMeridiem(this._AM)) {
              this.meridiems.unshift(this.meridiems.pop());
            }
          }
        } else {
          this.selectPrevious(this._spinnerMode === this.SPINNER_HOUR_MODE);
        }
      }
      this.cdref.markForCheck();
    }
  }

  /**
   * Handler de mouseup ou touchend pour fin de drag
   *
   * Suppression des listeners initialisés au mousedown
   * @param e Evenement de fin de drag
   */
  private onDragEnd(e:Event) {
    this.document.removeEventListener('mouseup', this._dragEndFunc, false);
    this.document.removeEventListener('mousemove', this._draggingFunc, false);
    this.document.removeEventListener('touchend', this._dragEndFunc, false);
    this.document.removeEventListener('touchmove', this._draggingFunc, false);
  }

  /** Prise de focus > focus sur la div des heures */
  public wizFocus(): void {
    this._hourDiv.nativeElement.focus();
  }

  /**
   * Ecoute clavier pour gestion de la navigation et de la sélection d'horaires
   * @param e Evènement key down
   */
  private keyDownHandler(e: KeyboardEvent) : void {
    if (e.key === VALUE_ESCAPE) { // Echap : fermeture
      if (this.overlayRef) {
        this.overlayRef.dispose();
      }
    } else if (e.key === VALUE_ENTER) { // Entrée : validation
      this.onValid();
    } else if (e.key === VALUE_TAB) { // Tab : on empeche la sortie de la popup
      const lastDiv: any = this._meridiemMode ? this._meridiemDiv.nativeElement : this._minuteDiv.nativeElement;
      if (e.target === lastDiv && !e.shiftKey) {
        e.preventDefault();
        this._hourDiv.nativeElement.focus();
      } else if (e.target === this._hourDiv.nativeElement && e.shiftKey) {
        e.preventDefault();
        lastDiv.focus();
      }
    } else { // Sur quel spinner est-on?
      let spinnerMode: number = NaN;
      if (e.target === this._hourDiv.nativeElement) {
        spinnerMode = this.SPINNER_HOUR_MODE;
      } else if (e.target === this._minuteDiv.nativeElement) {
        spinnerMode = this.SPINNER_MINUTE_MODE;
      } else if (e.target === this._meridiemDiv.nativeElement) {
        spinnerMode = this.SPINNER_MERIDIEM_MODE;
      }
      if (spinnerMode > 0) {
        this.onSpinnerKeyDown(e, spinnerMode);
      }
    }
  }

  /**
   * Déroulement des spinners et navigation par clavier
   * @param event Evenement clavier
   * @param pSpinnerMode Quel est le spinner cible
   */
  private onSpinnerKeyDown(event: KeyboardEvent, pSpinnerMode: number): void {
    // Haut ou bas: Sélection d'élément dans le spinner sélectionné
    if (event.key === VALUE_DOWN || event.key === VALUE_UP) {
      // Meridiem
      if (pSpinnerMode === this.SPINNER_MERIDIEM_MODE) {
        if (event.key === VALUE_DOWN && this.meridiemValue === this._PM) {
          if (this.switchMeridiem(this._AM)) {
            this.meridiems.unshift(this.meridiems.pop());
          }
        } else if (event.key === VALUE_UP && this.meridiemValue === this._AM) {
          if (this.switchMeridiem(this._PM)) {
            this.meridiems.push(this.meridiems.shift());
          }
        }
      } else if (event.key === VALUE_DOWN) {
        this.selectPrevious(pSpinnerMode == this.SPINNER_HOUR_MODE);
      } else {
        this.selectNext(pSpinnerMode == this.SPINNER_HOUR_MODE);
      }
      this.cdref.markForCheck(); // Mise à jour visuelle
    } else if (event.key === VALUE_LEFT) { // Gauche et droite: navigation entre les spinners
      if (pSpinnerMode === this.SPINNER_MERIDIEM_MODE) {
        this._minuteDiv.nativeElement.focus();
      } else if (pSpinnerMode === this.SPINNER_MINUTE_MODE) {
        this._hourDiv.nativeElement.focus();
      }
    } else if (event.key === VALUE_RIGHT) {
      if (pSpinnerMode === this.SPINNER_HOUR_MODE) {
        this._minuteDiv.nativeElement.focus();
      } else if (pSpinnerMode === this.SPINNER_MINUTE_MODE && this._meridiemDiv) {
        this._meridiemDiv.nativeElement.focus();
      }
    }
  }

  /* ########### VALIDATION ############## */

  /**
   * Handler du bouton de validation
   */
  public onValid(): void {
    if (this.wizValidHourCallBack) {
      this.wizValidHourCallBack(this.wizHourValue);
    }
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }
}
